import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { TEAMS_AVAILABLE } from '../tenant/teams-available';

/**
 * Which tenant the app is acting as — personal (`null`) or a specific team.
 *
 * Client state (CLAUDE.md > Frontend state): no endpoint returns "what the
 * user currently has selected", it's a UI choice. Persisted to localStorage
 * so a reload stays on the team you were looking at, the same reasoning as
 * the session token.
 *
 * `apiFetch` reads this outside React via `useScopeStore.getState()` to
 * attach `x-team-id` on every request — personal scope sends no scope header
 * at all, which the API already treats as "act as my own account" for every
 * existing caller (extension, older clients).
 */
interface ScopeState {
  selectedTeamId: string | null;
  selectTeam: (teamId: string | null) => void;
}

const initial = { selectedTeamId: null };

export const useScopeStore = create<ScopeState>()(
  persist(
    (set) => ({
      ...initial,
      selectTeam: (teamId) => {
        set({ selectedTeamId: TEAMS_AVAILABLE ? teamId : null });
      },
    }),
    {
      // A team picked before teams were switched off must not come back from
      // localStorage: `apiFetch` would keep sending its header, with no
      // switcher left on screen to leave it.
      merge: (persisted, current) =>
        TEAMS_AVAILABLE
          ? { ...current, ...(persisted as Partial<ScopeState>) }
          : current,
      name: 'floto.scope',
    },
  ),
);

/** The store is a module singleton, so tests must reset it between cases. */
export function resetScopeStore(): void {
  useScopeStore.setState(initial);
}
